import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md card-surface p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <span className="text-lg font-semibold">CCAMS</span>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose a strong password with at least 8 characters.
        </p>

        {done ? (
          <div
            data-testid="reset-success"
            className="mt-6 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 text-green-800 px-3 py-2 text-sm dark:border-green-900/50 dark:bg-green-950/40 dark:text-green-300"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="token">Reset token</Label>
              <Input
                id="token"
                data-testid="reset-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="pw">New password</Label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="pw"
                  data-testid="reset-password"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pw2">Confirm password</Label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="pw2"
                  data-testid="reset-password-confirm"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid="reset-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}

        <Link
          to="/login"
          className="mt-6 inline-block text-sm text-muted-foreground hover:text-foreground"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
